import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type { PropsWithChildren } from "react";
import { Menu, useSidebarState } from "react-admin";
import { Box, Divider, List, ListSubheader, Typography } from "@mui/material";

export const appMenuSx: SxProps<Theme> = (theme) => ({
  height: "100%",
  py: 2.5,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,

  "& .RaMenuItemLink-root": {
    minHeight: 44,
    borderRadius: 1.5,

    mx: 2.5,
    my: 0.25,
    px: 1.5,

    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",

    transition: theme.transitions.create(
      ["background-color", "color", "box-shadow"],
      { duration: theme.transitions.duration.shorter },
    ),
  },

  "& .RaMenuItemLink-root > .MuiTypography-root": {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    lineHeight: 1.2,
    margin: 0,
    minHeight: "100%",
  },

  "& .RaMenuItemLink-icon": {
    minWidth: 38,
    color: theme.palette.text.secondary,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  "& .RaMenuItemLink-icon svg": {
    fontSize: 21,
  },

  "& .RaMenuItemLink-primaryText": {
    display: "flex",
    alignItems: "center",
    lineHeight: 1.25,
    fontSize: 16,
  },

  "& .RaMenuItemLink-root:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.055),
  },

  "& .RaMenuItemLink-root.RaMenuItemLink-active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  "& .RaMenuItemLink-root.RaMenuItemLink-active .RaMenuItemLink-icon": {
    color: theme.palette.primary.main,
  },
});

export const appMenuListSx: SxProps<Theme> = {
  py: 0,
};

export const appMenuSectionHeaderSx: SxProps<Theme> = {
  bgcolor: "transparent",

  px: 3.75,
  pt: 1.5,
  pb: 1,

  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.4,
  textTransform: "uppercase",
  color: "text.secondary",
};

export const appMenuDividerSx: SxProps<Theme> = (theme) => ({
  my: 2,
  mx: 3,
  borderColor: alpha(theme.palette.divider, 0.8),
});

export type AppMenuProps = PropsWithChildren<{
  sx?: SxProps<Theme>;
}>;

export function AppMenu({ children, sx, ...props }: AppMenuProps) {
  const [open] = useSidebarState();

  return (
    <Menu
      {...props}
      sx={[
        appMenuSx,
        !open && {
          py: 2,

          "& .RaMenuItemLink-root": {
            justifyContent: "center",
            px: 1,
            mx: 1,
          },

          "& .RaMenuItemLink-icon": {
            minWidth: 0,
          },

          "& .RaMenuItemLink-icon svg": {
            fontSize: 28,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Menu>
  );
}

type AppMenuSectionProps = PropsWithChildren<{
  title?: string;
}>;

export function AppMenuSection({ title, children }: AppMenuSectionProps) {
  const [open] = useSidebarState();

  return (
    <List
      disablePadding
      subheader={
        open && title ? (
          <ListSubheader
            component="div"
            disableSticky
            sx={appMenuSectionHeaderSx}
          >
            {title}
          </ListSubheader>
        ) : undefined
      }
      sx={appMenuListSx}
    >
      {children}
    </List>
  );
}

export function AppMenuDivider() {
  return <Divider sx={appMenuDividerSx} />;
}

export function AppMenuBrand() {
  const [open] = useSidebarState();

  return (
    <Box
      sx={(theme) => ({
        height: 52,
        mt: 2,
        mb: 2.5,
        px: open ? 3 : 0,

        display: "inline-flex",
        alignItems: "center",
        justifyContent: open ? "flex-start" : "center",
        color: theme.palette.text.primary,
        userSelect: "none",

        "& .AppMenuBrand-content": {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          gap: theme.spacing(1.25),
          minWidth: 0,
        },

        "& .AppMenuBrand-logo": {
          width: 34,
          height: 34,
          display: "block",
          flexShrink: 0,
        },

        "& .AppMenuBrand-text": {
          fontWeight: 800,
          letterSpacing: -0.5,
          lineHeight: 1,
          fontSize: 34,
        },
      })}
      {...(!open
        ? {
            role: "img",
            "aria-label": "Logami",
          }
        : {})}
    >
      <Box className="AppMenuBrand-content">
        <Box
          component="img"
          src="/logami.svg"
          alt=""
          aria-hidden="true"
          className="AppMenuBrand-logo"
        />

        {open && (
          <Typography
            variant="h5"
            component="div"
            className="AppMenuBrand-text"
          >
            Logami
          </Typography>
        )}
      </Box>
    </Box>
  );
}
